import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import {
  fetchOrgLlmSettings,
  saveOrgLlmSettings,
  type OrgLlmSettings,
} from '../api/bridge'
import { useBiSource } from '../state/BiSourceContext'

type Props = {
  open: boolean
  onClose: () => void
}

type ProviderId = 'openrouter' | 'openai' | 'anthropic'

const KEY_HINTS: Record<ProviderId, { label: string; placeholder: string }> = {
  openrouter: { label: 'Chave OpenRouter', placeholder: 'sk-or-v1-…' },
  openai: { label: 'Chave OpenAI', placeholder: 'sk-…' },
  anthropic: { label: 'Chave Anthropic', placeholder: 'sk-ant-…' },
}

export function OrgLlmSettingsDialog({ open, onClose }: Props) {
  const { activeSource } = useBiSource()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [settings, setSettings] = useState<OrgLlmSettings | null>(null)
  const [billingMode, setBillingMode] = useState<'platform' | 'byok'>('platform')
  const [provider, setProvider] = useState<ProviderId>('openrouter')
  const [defaultModel, setDefaultModel] = useState('openai/gpt-4.1-mini')
  const [allowed, setAllowed] = useState<string[]>([])
  const [apiKey, setApiKey] = useState('')
  const [budgetUsd, setBudgetUsd] = useState('')
  const [softPct, setSoftPct] = useState('80')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setErr(null)
    setApiKey('')
    fetchOrgLlmSettings({ sourceId: activeSource?.id || 'fiesta' })
      .then((data) => {
        if (cancelled) return
        setSettings(data)
        setBillingMode(data.billing_mode === 'byok' ? 'byok' : 'platform')
        const p = (data.provider || 'openrouter') as ProviderId
        setProvider(p === 'openai' || p === 'anthropic' ? p : 'openrouter')
        setDefaultModel(data.default_model)
        setAllowed(data.allowed_models?.length ? data.allowed_models : [])
        setBudgetUsd(
          data.monthly_budget_usd != null && data.monthly_budget_usd > 0
            ? String(data.monthly_budget_usd)
            : '',
        )
        setSoftPct(String(data.soft_alert_pct ?? 80))
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Falha ao carregar')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, activeSource?.id])

  const models = useMemo(() => {
    const list = settings?.product_models || []
    // Backend já filtra por provider actual; se o utilizador mudou provider localmente
    // sem guardar, filtramos pelo estado local.
    return list.filter((m) => !m.provider || m.provider === provider)
  }, [settings?.product_models, provider])

  const providers = settings?.providers || [
    { id: 'openrouter', label: 'OpenRouter' },
    { id: 'openai', label: 'OpenAI' },
    { id: 'anthropic', label: 'Anthropic' },
  ]

  const effectiveProvider: ProviderId =
    billingMode === 'platform' ? 'openrouter' : provider

  const keyHint = KEY_HINTS[effectiveProvider]

  function onProviderChange(next: ProviderId) {
    setProvider(next)
    // Modelos do catálogo local só existem após save; limpar seleção até reload.
    // Se product_models tiver entradas do novo provider (após fetch antigo), filtra.
    const nextModels = (settings?.product_models || []).filter(
      (m) => m.provider === next,
    )
    if (nextModels.length) {
      const ids = nextModels.map((m) => m.id)
      setAllowed(ids)
      setDefaultModel(ids[0])
    } else {
      setAllowed([])
    }
  }

  async function onSave() {
    setSaving(true)
    setErr(null)
    try {
      const body: Parameters<typeof saveOrgLlmSettings>[0] = {
        source_id: activeSource?.id || 'fiesta',
        org_id: settings?.org_id,
        billing_mode: billingMode,
        provider: billingMode === 'platform' ? 'openrouter' : provider,
        default_model: defaultModel,
        allowed_models: allowed.length ? allowed : undefined,
        monthly_budget_usd: budgetUsd.trim() === '' ? '' : Number(budgetUsd),
        soft_alert_pct: Number(softPct) || 80,
      }
      if (billingMode === 'byok' && apiKey.trim()) {
        body.api_key = apiKey.trim()
      }
      const saved = await saveOrgLlmSettings(body)
      if (saved.error) throw new Error(saved.error)
      setSettings(saved)
      setApiKey('')
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao guardar')
    } finally {
      setSaving(false)
    }
  }

  async function onResetUsage() {
    setSaving(true)
    setErr(null)
    try {
      const saved = await saveOrgLlmSettings({
        source_id: activeSource?.id || 'fiesta',
        org_id: settings?.org_id,
        reset_usage: true,
        status: 'active',
      })
      if (saved.error) throw new Error(saved.error)
      setSettings(saved)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Falha ao repor ciclo')
    } finally {
      setSaving(false)
    }
  }

  function toggleModel(id: string) {
    setAllowed((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif' }}>
        LLM da organização
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2">A carregar…</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Org: <strong>{settings?.org_id || '—'}</strong>
              {activeSource?.name ? ` · fonte ${activeSource.name}` : ''}
            </Typography>
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Facturação
              </Typography>
              <RadioGroup
                value={billingMode}
                onChange={(e) => {
                  const mode = e.target.value as 'platform' | 'byok'
                  setBillingMode(mode)
                  if (mode === 'platform') setProvider('openrouter')
                }}
              >
                <FormControlLabel
                  value="platform"
                  control={<Radio size="small" />}
                  label="Créditos da plataforma (OpenRouter Fiesta)"
                />
                <FormControlLabel
                  value="byok"
                  control={<Radio size="small" />}
                  label="BYOK — chave do cliente"
                />
              </RadioGroup>
            </FormControl>
            {billingMode === 'byok' && (
              <TextField
                select
                label="Provider"
                size="small"
                value={provider}
                onChange={(e) => onProviderChange(e.target.value as ProviderId)}
                helperText="OpenAI e Anthropic são nativos (sem OpenRouter)."
              >
                {providers.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {billingMode === 'byok' && (
              <TextField
                label={
                  settings?.has_api_key
                    ? `Nova chave (actual ••••${settings.api_key_last4 || '****'})`
                    : keyHint.label
                }
                type="password"
                size="small"
                fullWidth
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={keyHint.placeholder}
                helperText="A chave nunca é mostrada de novo. Sem chave em BYOK o chat recusa — sem fallback."
              />
            )}
            <TextField
              select
              label="Modelo padrão"
              size="small"
              value={defaultModel}
              onChange={(e) => setDefaultModel(e.target.value)}
              disabled={!models.length}
            >
              {models.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
            <FormGroup>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Modelos permitidos
              </Typography>
              {models.map((m) => (
                <FormControlLabel
                  key={m.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={allowed.includes(m.id)}
                      onChange={() => toggleModel(m.id)}
                    />
                  }
                  label={m.label}
                />
              ))}
              {!models.length && (
                <Typography variant="caption" color="text.secondary">
                  Guarde o provider para carregar o catálogo de modelos.
                </Typography>
              )}
            </FormGroup>
            <TextField
              label="Teto mensal (USD)"
              size="small"
              type="number"
              value={budgetUsd}
              onChange={(e) => setBudgetUsd(e.target.value)}
              placeholder="vazio = sem limite"
              helperText="Hard stop a 100%. Soft alert conforme % abaixo."
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Soft alert (%)"
              size="small"
              type="number"
              value={softPct}
              onChange={(e) => setSoftPct(e.target.value)}
              inputProps={{ min: 1, max: 99, step: 1 }}
            />
            {usageBlock(settings)}
            {err && (
              <Typography variant="body2" color="error">
                {err}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          color="warning"
          onClick={onResetUsage}
          disabled={loading || saving}
        >
          Repor ciclo
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={onSave} disabled={loading || saving}>
            Guardar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

function usageBlock(settings: OrgLlmSettings | null) {
  const usage = settings?.usage
  if (!usage) return null
  return (
    <Typography variant="body2" color="text.secondary">
      Uso {usage.period || '—'}:{' '}
      <strong>${Number(usage.spent_usd || 0).toFixed(4)}</strong>
      {usage.budget_usd != null
        ? ` / $${Number(usage.budget_usd).toFixed(2)} (${usage.pct ?? 0}%)`
        : ' (sem teto)'}
      {' · '}
      {usage.calls || 0} chamadas
      {settings?.status === 'blocked' ? ' · BLOQUEADO' : ''}
    </Typography>
  )
}
